import axios from 'axios';

import { appConf } from '../appConf';
import { MAINTANANCE_PAGE_PATH, RESET_PASSWORD_PAGE_PATH } from '../constants/appPathConst';
import { STORAGE_DEALER } from '../constants/storageConst';
import { performLogout, retrieveAuthToken } from '../generalUtils/rbacUtils';
import { showMsg } from './library';
import { store } from '../index';
import {
  handleLayoutSetUserInfo,
  handleLayoutSetUserInfoSession
} from '../containers/MainLayout/mainLayoutSlice';
import { handleUnSetAuthToken } from '../containers/Auth/loginSlice';
import { NOPERMISSION } from '../constants/messageConst';

// const client = axios.create({ baseURL: appConf.api_url });

let axiosInstances = {};

const createInstance = apipath => {
  const controller = new AbortController();
  const customAxiosInstance = {
    controller,
    instance: axios.create({
      baseURL: appConf.api_url,
      signal: controller.signal,
      url: apipath
    })
  };
  return customAxiosInstance;
};
const getInstance = (apipath, pathname) => {
  // Check if the pathname exists in axiosInstances
  if (!axiosInstances[pathname]) {
    axiosInstances[pathname] = {}; // Initialize object for this pathname
  }

  // Check if a specific API path instance exists
  if (axiosInstances[pathname][apipath]) {
    const instanceData = axiosInstances[pathname][apipath];
    if (!instanceData.controller.signal.aborted) {
      return instanceData.instance; // Return the existing instance
    }
  }

  // Create a new instance if none exists or if the controller has been aborted
  const customAxiosInstance = createInstance(apipath);
  axiosInstances[pathname][apipath] = customAxiosInstance; // Store it
  return customAxiosInstance.instance;
};

const ApiService = options => {
  options.responseType = options.responseType || 'json';
  options.timeout = options.timeout || 1200000;

  const instance = getInstance(options.url, window.location.pathname);

  // Direct link to dealer
  const params = new URLSearchParams(window.location.search);
  const dealerId = parseInt(Number(params.get(STORAGE_DEALER)));
  let current = sessionStorage.getItem(STORAGE_DEALER);
  if (dealerId && current?.value !== dealerId) {
    sessionStorage.removeItem(STORAGE_DEALER);
  }

  let userToken = retrieveAuthToken();
  if (userToken != null) {
    options.headers = { ...options.headers, Authorization: userToken };
  }
  options.headers = { ...options.headers, 'x-App-Device': 'browser' };

  return instance(options)
    .then(response => {
      let res = response.data;
      if (res.status === false) {
        // Maintainance mode on backend
        if (res.type === 'MAINTAINANCE') {
          setTimeout(() => {
            window.location = `${appConf.site_url}${MAINTANANCE_PAGE_PATH.slice(1)}`;
          }, 1000);
        }
        // Session expired handling
        if (res.type === 'AUTH_EXPIRED') {
          performLogout();
          setTimeout(() => {
            store.dispatch(handleLayoutSetUserInfo(''));
            store.dispatch(handleUnSetAuthToken());
            store.dispatch(handleLayoutSetUserInfoSession(''));
          }, 1000);
        }
        // Enforce secure password
        if (res.type === 'RESET') {
          performLogout();
          setTimeout(() => {
            window.location = `${appConf.site_url}${RESET_PASSWORD_PAGE_PATH.slice(1)}/${res?.err?.token}`;
          }, 1000);
        }
      }
      // Proceed with data
      return Promise.resolve(res);
    })
    .catch(error => {
      if (error.message === 'canceled') {
        return Promise.reject({ data: null, status: false, message: '' });
      }
      const errorInfo = {
        message: error?.message || 'Unknown error',
        config: error.config || 'No config',
        status: error?.response?.status || 'No status',
        data: error?.response?.data || 'No data',
        headers: error?.response?.headers || 'No headers'
      };

      if (error?.response?.data?.type === 'ROLE_PERMISSION' && error?.response?.status === 403) {
        showMsg(NOPERMISSION, 'info');
      } else {
        console.error('ApiService Failed:', errorInfo);
      }

      return Promise.reject(error.response || error?.message || '');
    });
};

// Function to cancel all pending requests
export const cancelPreviousPageRequests = previousPath => {
  const pathInstances = axiosInstances[previousPath];

  // Check if there are any instances for the previous path
  if (pathInstances) {
    // Iterate over all API paths and abort their controllers
    Object.keys(pathInstances).forEach(apipath => {
      const instance = pathInstances[apipath];
      if (instance?.controller) {
        return instance.controller.abort();
      }
    });
  } else {
    if (previousPath) {
      console.log(`No previous requests found for path: ${previousPath}`);
    }
  }
};

export default ApiService;
